import { LOG } from '@/etc/log'
const log = LOG.m('store::ui')

export default {
  namespaced: true,
  state: {
    screenWidth: 0,
    mouse: { page: { x: 0, y: 0 }, client: { x: 0, y: 0 } },
    animation: {
      resize: false,
      height: 150
    },
    busy: false,
    keys: {},
    dnd: null,
    doHotkeys: false,
    // Mirrors the animation panel's expanded state so EditorView can drive the
    // editor scroll shift without reaching into a deeply nested child.
    keyframesPanelOpen: false,
    // Why the panel last opened: 'selection' (a clip got selected while the
    // panel was expandable) or 'manual' (chevron/explicit open). Selection
    // opens must not auto-shift the editor scroll.
    keyframesPanelOpenCause: 'manual',
    // Registered by EditorView on mount so store actions (cmd/toggleFullscreen)
    // can drive the scroll shift and await its animation before switching
    // layouts. Same pattern as project.editorInstance.
    editorScrollApi: null,
    fileDrop: {
      active: false,
      count: 0
    },
    dialogs: {
      invite: false
    }
  },
  getters: {
    isSmall: (store) => {
      return store.screenWidth < 900
    }
  },
  mutations: {
    doHotkeys: (state, next) => {
      state.doHotkeys = next
    },
    busy: (state, next) => {
      state.busy = next
    },
    screenWidth: (state, next) => {
      state.screenWidth = next
    },
    // Accepts either { open, cause } or a bare boolean (treated as 'manual').
    keyframesPanelOpen: (state, next) => {
      if (next && typeof next === 'object') {
        state.keyframesPanelOpen = !!next.open
        state.keyframesPanelOpenCause = next.cause || 'manual'
        return
      }
      state.keyframesPanelOpen = !!next
      state.keyframesPanelOpenCause = 'manual'
    },
    editorScrollApi: (state, next) => {
      state.editorScrollApi = next
    },
    pageMouse: (state, { pageX, pageY, clientX, clientY }) => {
      state.mouse.page.x = pageX
      state.mouse.page.y = pageY
      state.mouse.client.x = clientX
      state.mouse.client.y = clientY
    },
    key: (state, { key, status }) => {
      state.keys[key] = status
    },
    dnd: (state, next) => {
      state.dnd = next
    },
    fileDrop: (state, next) => {
      state.fileDrop.active = !!next.active
      state.fileDrop.count = typeof next.count === 'number' ? next.count : state.fileDrop.count
    },
    dialog: (state, { dialog, show }) => {
      state.dialogs[dialog] = show
    }
  },
  actions: {
    init: async (store) => {
      window.onresize = () => {
        store.commit('screenWidth', window.innerWidth)
      }
      store.commit('screenWidth', window.innerWidth)
      // Ignore all editor hotkeys while focus is inside any editable field
      const isEditableElement = (el) => {
        if (!el) return false
        if (el.isContentEditable) return true
        const tag = (el.tagName || '').toLowerCase()
        return tag === 'input' || tag === 'textarea' || tag === 'select' ||
          (el.getAttribute && el.getAttribute('role') === 'textbox')
      }

      // While the node canvas overlay is fullscreen it owns the keyboard
      // (node/edge delete, arrow camera pan, ...). Only playback and toggle
      // keys may reach the editor command table, so e.g. Delete deletes the
      // focused node without also dropping the selected editor clip.
      const FULLSCREEN_PASSTHROUGH = new Set(['Space', 'Tab'])
      const blockedByFullscreen = (e) =>
        !!store.rootState.project?.fullScreen && !FULLSCREEN_PASSTHROUGH.has(e.code)

      document.addEventListener('keydown', e => {
        // console.log('KD', e)
        if (!store.state.doHotkeys)
          return
        if (isEditableElement(e.target) || isEditableElement(document.activeElement))
          return
        if (blockedByFullscreen(e))
          return
        if (e.code === 'Space' || e.code === 'Tab') {
          e.stopPropagation()
          e.preventDefault()
        }
        store.commit('key', { key: e.code, status: true })
        store.dispatch('cmd/hotkey', e, { root: true })
        // log.info('hotkey', e.code)
      })
      document.addEventListener('keyup', e => {
        if (!store.state.doHotkeys)
          return
        if (isEditableElement(e.target) || isEditableElement(document.activeElement))
          return
        if (blockedByFullscreen(e))
          return
        store.commit('key', { key: e.code, status: false })
        store.dispatch('cmd/hotkeyUp', e, { root: true })
      })
      log.info('init')
    },
    clickOutside: (store) => {
      log.debug('click outside')
      store.commit('project/dnd', null, { root: true })
    }
  }
}
